import { useCallback, useEffect, useState } from "react";

interface UseCountdownProps {
  initialSeconds: number;
  onComplete?: () => void;
}

interface UseCountdownReturn {
  seconds: number;
  isRunning: boolean;
  start: () => void;
  pause: () => void;
  reset: () => void;
  resetTo: (newSeconds: number) => void;
}

/**
 * A hook that provides countdown functionality with the ability to start, pause, and reset.
 *
 * @param initialSeconds The initial number of seconds for the countdown
 * @param onComplete Optional callback function to be called when the countdown reaches zero
 * @returns An object containing the current seconds, running state, and control functions
 */
export const useCountdown = ({
  initialSeconds,
  onComplete,
}: UseCountdownProps): UseCountdownReturn => {
  const [seconds, setSeconds] = useState(initialSeconds);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    let intervalId: number | null = null;

    if (isRunning) {
      intervalId = window.setInterval(() => {
        setSeconds((prevSeconds) => {
          if (prevSeconds <= 1) {
            setIsRunning(false);
            onComplete?.();
            return 0;
          }
          return prevSeconds - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isRunning, onComplete]);

  const start = useCallback(() => {
    if (seconds > 0) {
      setIsRunning(true);
    }
  }, [seconds]);

  const pause = useCallback(() => {
    setIsRunning(false);
  }, []);

  const reset = useCallback(() => {
    setIsRunning(false);
    setSeconds(initialSeconds);
  }, [initialSeconds]);

  const resetTo = useCallback((newSeconds: number) => {
    setIsRunning(false);
    setSeconds(newSeconds);
  }, []);

  return { seconds, isRunning, start, pause, reset, resetTo };
};
